import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { extractCaseId, filterCases } from '../lib/search-helpers';
import { useSearch } from './use-search';
import { useMyCases } from './use-cases';

export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQueryRaw] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeCaseId = extractCaseId(pathname);

  const { results: searchResults, isSearching, isConfigured } = useSearch(query, activeCaseId);
  const { data: myCases } = useMyCases();

  // Klientside sakstreff alltid; Typesense dekker ikke sakslisten.
  const caseResults = useMemo(() => filterCases(myCases, query), [myCases, query]);
  const results = useMemo(() => {
    if (!isConfigured) return caseResults;
    return [...caseResults, ...searchResults];
  }, [caseResults, isConfigured, searchResults]);

  // Nullstill aktiv-indeks ved nye resultater
  useEffect(() => setActiveIndex(0), [results]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQueryRaw('');
    setActiveIndex(0);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    setQueryRaw('');
    setActiveIndex(0);
  }, []);

  const setQuery = useCallback((q: string) => {
    setQueryRaw(q);
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) close();
        else open();
      }
      if (!isOpen) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(1, results.length));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + Math.max(1, results.length)) % Math.max(1, results.length));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, open, close, results.length]);

  return {
    isOpen,
    query,
    results,
    isSearching,
    isConfigured,
    activeIndex,
    open,
    close,
    setQuery,
    setActiveIndex,
  };
}
